import { useIntl } from 'react-intl';
import { mutate } from 'swr';

import ButtonPack from '@/components/app/cards/ButtonPack';
import CardCarousel from '@/components/app/cards/CardCarousel';
import CardGrid from '@/components/app/cards/CardGrid';
import { isSupportedPackItem } from '@/components/app/cards/ContentCard';
import PackRowList from '@/components/app/cards/PackRowList';
import TagsList from '@/components/app/cards/TagsList';
import ToutPack from '@/components/app/cards/ToutPack';
import PackClassLink from '@/components/app/ui/PackClassLink';
import Loader from '@/components/Loader';
import { HandoffScreenConfig } from '@/components/SimplifiedSignup/Handoff/types';
import UpsellBanner from '@/components/UpsellBanner';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import useLockedContentUpsell from '@/hooks/app/useLockedContentUpsell';
import { usePackItemAttributes } from '@/hooks/app/usePackItemAttributes';
import { useUpsellBannerContent } from '@/hooks/app/useUpsellBannerContent/useUpsellBannerContent';
import { useThemeState, useUserState } from '@/hooks/store';
import { useTimeOfDay } from '@/hooks/utils/useTimeOfDay';
import genericMessages from '@/messages/messages';
import { getGreetingHeader } from '@/utils/app/getGreeting';

import { usePacksContext } from '../../layout/PacksProvider';
import ParagraphPack from '../ParagraphPack';
import { PageSubtitle, PageTitle } from './styles';
import { isUrl, Pack, PackCardItem, PacksListProps, TextItem, UrlItem } from './types';

// `currentPackItem` is `null` outside the locked-content upsell flow, so this stands in for it
// the rest of the time. Hoisted rather than built inline so re-renders reuse one reference,
// which keeps this call site safe for any reference-sensitive consumer of the hook.
const EMPTY_PACK_ITEM = {} as PackCardItem;

const PacksList = ({
	packFeedName,
	packs,
	tags,
	activeTag,
	isLoading,
	showGreeting = false,
	hideUpsellBanner = false,
	isClassPage = false,
}: PacksListProps) => {
	const timeOfDay = useTimeOfDay();
	const { formatMessage } = useIntl();
	const user = useUserState();

	function displaySupportedPack(pack: Pack) {
		switch (pack.display_type) {
			case 'carousel':
			case 'banner':
			case 'banner-carousel':
				return <CardCarousel pack={pack} displayType={pack.display_type} packFeedName={packFeedName} />;
			case 'grid':
				return <CardGrid pack={pack} />;
			case 'row':
				return <PackRowList pack={pack} packFeedName={packFeedName} isPageHeading={isClassPage} />;
			case 'link':
				// TODO: This should happen on the backend (or we should support narrator classes)
				return !pack.pack_class.includes('narrator') ? (
					<PackClassLink pack={pack} cta={pack.title} theme="large" />
				) : null;
			case 'carousel-landscape':
				// These are recommended collections, which we don't support yet
				return null;
			case 'quick-nav':
				// not supported on home yet
				return null;
			case 'button': {
				const buttonItem = pack.items.find(isSupportedPackItem);
				if (!buttonItem) {
					return null;
				}
				return <ButtonPack pack={pack} item={buttonItem} />;
			}
			case 'tout':
			case 'banner-promo': {
				// Any item may carry url: null, so match on a non-empty url rather than on the key.
				const urlItem = pack.items.find((item): item is UrlItem => isUrl(item) && Boolean(item.url));
				if (!urlItem) {
					return null;
				}
				return <ToutPack pack={pack} item={urlItem} packFeedName={packFeedName} />;
			}
			case 'paragraph': {
				const textItem = pack.items.find(item => item.type === 'text');
				if (!textItem) {
					return null;
				}
				const { text } = textItem as TextItem;
				if (!text) {
					return null;
				}
				return <ParagraphPack id={pack.id} text={text} />;
			}
			default:
				return null;
		}
	}

	const { setIsModalOpen } = useSimplifiedSignupModalContext();
	const { flowConfig } = useLockedContentUpsell();
	const { upsellCopy } = useUpsellBannerContent();

	const {
		currentPackItem,
		pageTitle: pageTitleFromPack,
		pageSubtitle: pageSubtitleFromPack,
	} = usePacksContext();

	const { handleUnlockedPackItemClick } = usePackItemAttributes({
		packItem: currentPackItem ?? EMPTY_PACK_ITEM,
	});

	const theme = useThemeState();
	const getPageTitle = (): string | null => {
		if (theme.webAppLayout === 'profile') {
			return formatMessage(genericMessages.profile);
		}

		if (showGreeting) return null;

		if (pageTitleFromPack) {
			return pageTitleFromPack;
		}

		return null;
	};

	const getPageSubtitle = (): string | null => {
		if (pageSubtitleFromPack) {
			return pageSubtitleFromPack;
		}

		return null;
	};

	const handoffCTA = (flowConfig?.screens?.handoff as HandoffScreenConfig)?.CTA;

	if (handoffCTA) {
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/immutability
		handoffCTA.onClick = async () => {
			await mutate([`packs/feeds/${packFeedName}`, packFeedName, user?.id]);
			await handleUnlockedPackItemClick?.();
			setIsModalOpen(false);
		};
	}

	return (
		<>
			{!hideUpsellBanner && upsellCopy && <UpsellBanner />}
			{showGreeting && (
				<PageTitle>
					{formatMessage(getGreetingHeader(timeOfDay)) + (user?.name ? `, ${user.name}` : '')}
				</PageTitle>
			)}
			{getPageTitle() && <PageTitle>{getPageTitle()}</PageTitle>}
			{getPageSubtitle() && <PageSubtitle>{getPageSubtitle()}</PageSubtitle>}
			{isLoading ? (
				<Loader />
			) : (
				<>
					{tags && tags.length > 0 && (
						<TagsList tags={tags} activeTag={activeTag} packFeedName={packFeedName} />
					)}
					{packs.map(pack => (
						<div key={pack.id} data-testid="pack-container">
							{displaySupportedPack(pack)}
						</div>
					))}
				</>
			)}
		</>
	);
};

export default PacksList;
